import { useState } from "react";
import clsx from "clsx";
import { CredentialEditModal } from "./modals/CredentialEditModal";
import {
  useHotelCredentials,
  useRemoveCredential,
} from "../hooks/useCredentials";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard";
import type {
  CredentialKind,
  HotelCredentialsPayload,
  ResolvedCredential,
} from "../types";

/**
 * The hotel's website and PMS logins, in the header of the page where
 * somebody is already diagnosing that hotel.
 *
 * It sits here because of what it replaces: an operator halfway through an
 * incident, leaving for a 60-entry shared doc, scrolling for a property name
 * that may be filed under its PMS instead. The values are rendered in the
 * clear — this page is already behind an admin JWT, and a masked field that
 * must be revealed before it can be read reintroduces the interruption the
 * panel exists to remove.
 *
 * Collapsed behind a disclosure until 2026-08-31, on the argument that a
 * block of secrets should not be permanently on screen during a screenshare.
 * Reversed deliberately: the panel is only worth having if it beats the doc
 * it replaces, and one that costs a click and a fetch first does not.
 */

const KIND_LABEL: Record<CredentialKind, string> = {
  pms: "PMS",
  website: "Website",
};

/**
 * Display order, owned here rather than taken from the payload: which login
 * an operator reaches for first is a question about this page, and the
 * backend's `CREDENTIAL_KINDS` order answers a different one.
 */
const CARD_ORDER: readonly CredentialKind[] = ["website", "pms"];

/**
 * The platform this hotel is on, read from the hotel's own record rather
 * than from anything stored beside the login. One fact, held once: a copy
 * typed in next to a password is a copy that goes stale silently, and it
 * goes stale exactly when it matters — on the hotel that has migrated off
 * the platform whose shared login it is still being offered.
 */
function platformOf(
  payload: HotelCredentialsPayload,
  kind: CredentialKind,
): string | null {
  return kind === "pms"
    ? payload.matchedOn.pms
    : payload.matchedOn.websiteBuilder;
}

const ROW_LABEL_CLASS = "text-xs text-gray-500 w-16 shrink-0";

function CopyField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const { copied, copy } = useCopyToClipboard(value);

  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className={ROW_LABEL_CLASS}>{label}</span>
      <button
        type="button"
        onClick={copy}
        data-tip="Click to copy"
        className={clsx(
          "text-sm text-content-primary text-left break-all hover:bg-[#F4F4F6] rounded px-1 -mx-1 transition-colors",
          mono && "font-mono",
        )}
      >
        {value}
      </button>
      {copied && <span className="text-xs text-success-dark">copied</span>}
    </div>
  );
}

function SecretRow({ cred }: { cred: ResolvedCredential }) {
  if (cred.password)
    return <CopyField label="Password" value={cred.password.value} mono />;

  // One state again now that the store holds these in the clear: either a
  // tier recorded one or nobody did.
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={ROW_LABEL_CLASS}>Password</span>
      <span className="text-sm text-content-tertiary italic">
        None recorded
      </span>
    </div>
  );
}

/**
 * The empty card, said in terms of what the operator should do next.
 *
 * This is the state a freshly deployed environment is in for every hotel, so
 * it has to carry more than "nothing here". The two causes need different
 * actions and are indistinguishable from the value alone: a hotel with no
 * platform on file cannot match a shared login no matter how many are
 * recorded, while a hotel whose platform IS known is simply waiting for that
 * platform's default to be entered once.
 */
function emptyMessage(kind: CredentialKind, platform: string | null): string {
  const what = kind === "pms" ? "PMS" : "website builder";
  return platform
    ? `Nothing recorded for this hotel, and no shared ${platform} login on file yet.`
    : `No ${what} on file for this hotel, so no shared login can match it either.`;
}

function CredentialCard({
  cred,
  platform,
  onEdit,
  onRemove,
  removing,
}: {
  cred: ResolvedCredential;
  platform: string | null;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  // Only an override can be removed — there is nothing hotel-specific to
  // drop when every field resolved from the platform default.
  const hasOverride = [
    cred.username,
    cred.password,
    cred.loginUrl,
    cred.note,
  ].some((f) => f?.provenance === "hotel-override");

  return (
    <div className="rounded-md border border-[#E9E9EB] bg-white p-2.5 min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-xs uppercase tracking-wider text-gray-500 shrink-0">
            {KIND_LABEL[cred.kind]}
          </span>
          <span
            className={clsx(
              "text-sm font-medium truncate",
              platform ? "text-content-primary" : "text-content-tertiary",
            )}
            data-tip={
              platform
                ? "Read from this hotel's own record — the shared login is matched on it"
                : undefined
            }
          >
            {platform ?? "—"}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasOverride && (
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              data-tip="Remove this hotel's own login — the platform default applies again. Does not blank the credential."
              className="text-xs text-content-tertiary hover:text-danger-dark disabled:opacity-50"
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-content-secondary hover:text-content-primary"
          >
            {cred.empty ? "Add" : "Edit"}
          </button>
        </div>
      </div>

      {cred.empty ? (
        <p className="text-sm text-content-tertiary">
          {emptyMessage(cred.kind, platform)}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {cred.username && (
            <CopyField label="Username" value={cred.username.value} />
          )}
          <SecretRow cred={cred} />
          {cred.loginUrl && (
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className={ROW_LABEL_CLASS}>Sign in</span>
              <a
                href={cred.loginUrl.value}
                target="_blank"
                rel="noopener noreferrer"
                data-tip={cred.loginUrl.value}
                className="text-sm text-brand-primary hover:underline truncate"
              >
                {cred.loginUrl.value}
              </a>
            </div>
          )}
          {/* The note is deliberately NOT on the card: the longest seeded
              one carries a 2FA sentence and a recovery code, and this card
              sits in the header where it must not push the verdicts below
              the fold. It lives in the edit modal instead. */}
        </div>
      )}
    </div>
  );
}

export function CredentialsStrip({ userId }: { userId: string | null }) {
  const [editing, setEditing] = useState<CredentialKind | null>(null);
  const query = useHotelCredentials(userId);
  const remove = useRemoveCredential(userId ?? "");

  // No matched platform user means no customer to hold credentials — the
  // meta line beside this already says "no platform user matched", so a
  // broken panel here would only repeat it worse.
  if (!userId) return null;

  const payload = query.data;

  return (
    // The header's middle slot, between the hotel's identity and its action
    // buttons. It claims the space between them, caps its own width so
    // neither neighbour is crushed, and wraps to a line of its own when the
    // viewport cannot seat all three.
    <div className="flex-1 min-w-[360px] max-w-2xl">
      {query.isLoading && (
        <p className="text-sm text-content-tertiary">Loading credentials…</p>
      )}

      {query.isError && (
        <p className="text-sm text-danger-dark">Could not load credentials.</p>
      )}

      {payload && (
        <div className="grid grid-cols-2 gap-2">
          {CARD_ORDER.flatMap((kind) =>
            payload.credentials.filter((c) => c.kind === kind),
          ).map((cred) => (
            <CredentialCard
              key={cred.kind}
              cred={cred}
              platform={platformOf(payload, cred.kind)}
              onEdit={() => setEditing(cred.kind)}
              onRemove={() => remove.mutate(cred.kind)}
              removing={remove.isPending}
            />
          ))}
        </div>
      )}

      {editing && payload && (
        <CredentialEditModal
          userId={userId}
          kind={editing}
          platform={platformOf(payload, editing)}
          current={payload.credentials.find((c) => c.kind === editing) ?? null}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
